import { useEffect, useState } from "react";
import type { Feature } from "../hooks/useFeatures";
import { useCreateFeature, useUpdateFeature } from "../hooks/useFeatures";

interface FeatureFormProps {
	feature?: Feature | null;
	onSuccess: () => void;
	onCancel: () => void;
}

const FEATURE_KEY_PATTERN = /^[A-Z][A-Z0-9_]*$/;
const MAX_FEATURE_KEY_LENGTH = 50;

function validateFeatureKey(value: string): string | null {
	if (!value) {
		return "Feature key is required";
	}
	if (value.length > MAX_FEATURE_KEY_LENGTH) {
		return `Feature key must be ${MAX_FEATURE_KEY_LENGTH} characters or less`;
	}
	if (!FEATURE_KEY_PATTERN.test(value)) {
		return "Must be uppercase letters, numbers, and underscores only. Must start with a letter.";
	}
	return null;
}

export function FeatureForm({ feature, onSuccess, onCancel }: FeatureFormProps) {
	const isEditing = !!feature;

	const [featureKey, setFeatureKey] = useState(feature?.featureKey ?? "");
	const [name, setName] = useState(feature?.name ?? "");
	const [description, setDescription] = useState(feature?.description ?? "");
	const [enabledForAll, setEnabledForAll] = useState(
		feature?.enabledForAll ?? false,
	);

	const [featureKeyError, setFeatureKeyError] = useState<string | null>(null);
	const [nameError, setNameError] = useState<string | null>(null);
	const [submitError, setSubmitError] = useState<string | null>(null);

	const createFeature = useCreateFeature();
	const updateFeature = useUpdateFeature();

	const isSubmitting = createFeature.isPending || updateFeature.isPending;

	// Reset form when feature changes (for edit mode)
	useEffect(() => {
		if (feature) {
			setFeatureKey(feature.featureKey);
			setName(feature.name);
			setDescription(feature.description ?? "");
			setEnabledForAll(feature.enabledForAll);
		}
	}, [feature]);

	const handleFeatureKeyChange = (value: string) => {
		// Auto-uppercase as user types
		const uppercased = value.toUpperCase();
		setFeatureKey(uppercased);
		setFeatureKeyError(null);
	};

	const handleNameChange = (value: string) => {
		setName(value);
		setNameError(null);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setSubmitError(null);

		// Validate
		let hasErrors = false;

		if (!isEditing) {
			const keyError = validateFeatureKey(featureKey);
			if (keyError) {
				setFeatureKeyError(keyError);
				hasErrors = true;
			}
		}

		if (!name.trim()) {
			setNameError("Name is required");
			hasErrors = true;
		}

		if (hasErrors) {
			return;
		}

		try {
			if (isEditing) {
				const result = await updateFeature.mutateAsync({
					featureKey: feature.featureKey,
					name: name.trim(),
					description: description.trim() || undefined,
					enabledForAll,
				});

				if (result.userErrors && result.userErrors.length > 0) {
					setSubmitError(result.userErrors[0].message);
					return;
				}
			} else {
				const result = await createFeature.mutateAsync({
					featureKey: featureKey.trim(),
					name: name.trim(),
					description: description.trim() || "",
					enabledForAll,
				});

				if (result.userErrors && result.userErrors.length > 0) {
					setSubmitError(result.userErrors[0].message);
					return;
				}
			}

			onSuccess();
		} catch (err) {
			console.error("Failed to save feature:", err);
			setSubmitError(
				err instanceof Error ? err.message : "Failed to save feature",
			);
		}
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
			<div className="card w-full max-w-md">
				<h3 className="text-lg font-semibold text-white">
					{isEditing ? `Edit Feature: ${feature.featureKey}` : "Create Feature"}
				</h3>

				<form onSubmit={handleSubmit} className="mt-4 space-y-4">
					{!isEditing && (
						<div>
							<label htmlFor="featureKey" className="label">
								Feature Key
							</label>
							<input
								id="featureKey"
								type="text"
								value={featureKey}
								onChange={(e) => handleFeatureKeyChange(e.target.value)}
								className={`input font-mono ${featureKeyError ? "border-red-500" : ""}`}
								placeholder="MY_FEATURE_KEY"
							/>
							{featureKeyError && (
								<p className="mt-1 text-sm text-red-400">{featureKeyError}</p>
							)}
							<p className="mt-1 text-xs text-gray-500">
								Uppercase letters, numbers, and underscores only. Cannot be
								changed after creation.
							</p>
						</div>
					)}

					<div>
						<label htmlFor="name" className="label">
							Name
						</label>
						<input
							id="name"
							type="text"
							value={name}
							onChange={(e) => handleNameChange(e.target.value)}
							className={`input ${nameError ? "border-red-500" : ""}`}
							placeholder="My Feature Name"
						/>
						{nameError && (
							<p className="mt-1 text-sm text-red-400">{nameError}</p>
						)}
					</div>

					<div>
						<label htmlFor="description" className="label">
							Description
						</label>
						<textarea
							id="description"
							value={description}
							onChange={(e) => setDescription(e.target.value)}
							className="input min-h-[80px]"
							placeholder="A brief description of what this feature does..."
						/>
					</div>

					<div className="flex items-center gap-3">
						<button
							type="button"
							role="switch"
							aria-checked={enabledForAll}
							onClick={() => setEnabledForAll(!enabledForAll)}
							className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-900 ${
								enabledForAll ? "bg-indigo-600" : "bg-gray-600"
							}`}
						>
							<span
								className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
									enabledForAll ? "translate-x-5" : "translate-x-0"
								}`}
							/>
						</button>
						<div>
							<span className="text-sm font-medium text-white">
								Enabled for All Users
							</span>
							<p className="text-xs text-gray-500">
								When enabled, this feature is active for everyone regardless of
								individual settings.
							</p>
						</div>
					</div>

					{submitError && (
						<div className="rounded-md bg-red-500/20 p-3 text-sm text-red-400">
							{submitError}
						</div>
					)}

					<div className="flex justify-end gap-3 pt-2">
						<button
							type="button"
							onClick={onCancel}
							className="btn-secondary"
							disabled={isSubmitting}
						>
							Cancel
						</button>
						<button
							type="submit"
							className="btn-primary"
							disabled={isSubmitting}
						>
							{isSubmitting
								? "Saving..."
								: isEditing
									? "Update Feature"
									: "Create Feature"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
